import React, { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Icons } from '@components/Icon';
import { IconTemplate } from '@components/Icon/IconTemplate';
import useTranslations from '@hooks/useTranslation';
import { publicAssets } from '@utils/url.utils';
import moment from 'moment/moment';
import { ROUTES, getImageBaseURL } from '../../constants';
import { ILecturerItem } from '../../interfaces/Lecturer';
import { lecturerApi } from '../../services/lecturer.service';
import {
    ArchiveName,
    Archives,
    Description,
    Event,
    EventDate,
    EventName,
    Events,
    Image,
    ImageSection,
    Information,
    Lecturer,
    Mail,
    MoreLink,
    Name,
    Position,
    SubTitle,
    Title,
    Wrapper,
} from './TrainingLecturer.elements';

export interface TrainingLecturerProps {
    isDetailPage?: boolean;
    lecturer?: ILecturerItem;
    openedCourseId?: string;
}

enum PARTS {
    TRAININGS = 'trainings',
    CONFERENCES = 'conferences',
    RECORDS = 'records',
}

const LIMIT_OF_EVENTS_PER_PAGE = 3;

const TrainingLecturerTemplate: FC<TrainingLecturerProps> = ({ isDetailPage = false, lecturer, openedCourseId }) => {
    const { t } = useTranslations();

    const router = useRouter();

    const [upcomingTrainings, setUpcomingTrainings] = useState([]);
    const [upcomingTrainingsCount, setUpcomingTrainingsCount] = useState(0);
    const [upcomingTrainingsLimit, setUpcomingTrainingsLimit] = useState(3);
    const [upcomingConferences, setUpcomingConferences] = useState([]);
    const [upcomingConferencesCount, setUpcomingConferencesCount] = useState(0);
    const [upcomingConferencesLimit, setUpcomingConferencesLimit] = useState(3);
    const [records, setRecords] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [recordsLimit, setRecordsLimit] = useState(3);

    const handleShowMoreClick = (part: PARTS) => {
        if (part === PARTS.TRAININGS) {
            lecturerApi
                .upcomingTrainings(lecturer.slug, upcomingTrainingsLimit + LIMIT_OF_EVENTS_PER_PAGE, 0)
                .then(({ data }) => {
                    setUpcomingTrainings(data?.results);
                    setUpcomingTrainingsCount(data?.total_count);
                    setUpcomingTrainingsLimit(upcomingTrainingsLimit + LIMIT_OF_EVENTS_PER_PAGE);
                });
        }

        if (part === PARTS.CONFERENCES) {
            lecturerApi
                .upcomingConferences(lecturer.slug, recordsLimit + LIMIT_OF_EVENTS_PER_PAGE, 0)
                .then(({ data }) => {
                    setUpcomingConferences(data?.results);
                    setUpcomingConferencesCount(data?.total_count);
                    setUpcomingConferencesLimit(recordsLimit + LIMIT_OF_EVENTS_PER_PAGE);
                });
        }

        if (part === PARTS.RECORDS) {
            lecturerApi.records(lecturer.slug, recordsLimit + LIMIT_OF_EVENTS_PER_PAGE, 0).then(({ data }) => {
                setRecords(data?.results);
                setRecordsCount(data?.total_count);
                setRecordsLimit(recordsLimit + LIMIT_OF_EVENTS_PER_PAGE);
            });
        }
    };

    useEffect(() => {
        lecturerApi
            .upcomingTrainings(lecturer.slug, upcomingTrainingsLimit, 0, openedCourseId ? [openedCourseId] : [])
            .then(({ data }) => {
                setUpcomingTrainings(data?.results);
                setUpcomingTrainingsCount(data?.total_count);
            });
        lecturerApi.upcomingConferences(lecturer.slug, upcomingConferencesLimit, 0).then(({ data }) => {
            setUpcomingConferences(data?.results);
            setUpcomingConferencesCount(data?.total_count);
        });
        lecturerApi.records(lecturer.slug, recordsLimit, 0, openedCourseId ? [openedCourseId] : []).then(({ data }) => {
            setRecords(data?.results);
            setRecordsCount(data?.total_count);
        });
    }, []);

    const formatEventDate = (from: Date, to: Date) => {
        const formattedFrom = moment(from);
        const formattedTo = moment(to);

        return formattedTo.diff(formattedFrom, 'days') > 0
            ? `${moment(from).format('DD. MM. YYYY')} - ${moment(to).format('DD. MM. YYYY')}`
            : moment(from).format('DD. MM. YYYY');
    };

    return (
        <Wrapper>
            <Title withoutBackground>{t('training.lecturer.title')}</Title>
            {lecturer && (
                <Lecturer>
                    <ImageSection>
                        {lecturer?.photo ? (
                            <Image src={`${getImageBaseURL()}/media/people/${lecturer._key}`} />
                        ) : (
                            <Image src={`${publicAssets('/avatar.svg')}`} />
                        )}
                    </ImageSection>
                    <Information>
                        <Name>
                            {lecturer.titles?.before_name ? `${lecturer.titles?.before_name} ` : ''}
                            {lecturer.name} {lecturer.surname}
                            {lecturer.titles?.after_name ? ` ${lecturer.titles?.after_name}` : ''}
                        </Name>
                        {lecturer.position && <Position>{lecturer.position}</Position>}
                        {lecturer.email && lecturer.email !== 'undefined' && <Mail href={`mailto:${lecturer.email}`}>{lecturer.email}</Mail>}
                        <Description dangerouslySetInnerHTML={{ __html: lecturer.annotation }} />
                    </Information>
                </Lecturer>
            )}
            {upcomingTrainingsCount > 0 && (
                <Events withoutBorder={upcomingConferencesCount === 0}>
                    <SubTitle>{t('training.lecturer.trainings')}</SubTitle>
                    {upcomingTrainings?.map((event) =>
                        event.eventDate?.map(
                            (
                                date: {
                                    eventDate: { from: Date; to: Date };
                                    isOnline: boolean;
                                    venues: { name: string; city: string }[];
                                },
                                key: number
                            ) => (
                                <Event key={key}>
                                    <EventDate key={key}>
                                        <strong>{formatEventDate(date.eventDate.from, date.eventDate.to)}</strong> |{' '}
                                        {date.isOnline ? 'Online' : date.venues?.[0]?.city || date.venues?.[0]?.name}
                                    </EventDate>
                                    <EventName
                                        onClick={() =>
                                            router.push({
                                                pathname: `${ROUTES.TRAINING}/${event.course.slug}`,
                                            })
                                        }
                                    >
                                        {event.course.title}
                                    </EventName>
                                </Event>
                            )
                        )
                    )}
                    {upcomingTrainings?.length < upcomingTrainingsCount && (
                        <MoreLink onClick={() => handleShowMoreClick(PARTS.TRAININGS)}>
                            {t('training.lecturer.showMore')}
                        </MoreLink>
                    )}
                </Events>
            )}
            {upcomingConferencesCount > 0 && (
                <Events withoutBorder={recordsCount === 0}>
                    <SubTitle>{t('training.lecturer.conferences')}</SubTitle>
                    {upcomingConferences?.map((event) =>
                        event.eventDate?.map(
                            (
                                date: { eventDate: { from: Date; to: Date }; venues: { name: string; city: string }[] },
                                key: number
                            ) => (
                                <Event key={key}>
                                    <EventDate key={key}>
                                        {formatEventDate(date.eventDate.from, date.eventDate.to)} |{' '}
                                        {date.venues?.[0]?.city || date.venues?.[0]?.name}
                                    </EventDate>
                                    <EventName
                                        onClick={() =>
                                            router.push({
                                                pathname: `${ROUTES.CONFERENCE}/${event.conference.slug}`,
                                            })
                                        }
                                    >
                                        {event.conference.title}
                                    </EventName>
                                </Event>
                            )
                        )
                    )}
                    {upcomingConferences?.length < upcomingConferencesCount && (
                        <MoreLink onClick={() => handleShowMoreClick(PARTS.CONFERENCES)}>
                            <IconTemplate name={Icons.ArrowDown} /> {t('training.lecturer.showMore')}
                        </MoreLink>
                    )}
                </Events>
            )}
            {recordsCount > 0 && (
                <Archives withoutBorder>
                    <SubTitle>{t('training.lecturer.records')}</SubTitle>
                    {records?.map((event, key: number) => (
                        <ArchiveName
                            key={key}
                            onClick={() =>
                                router.push({
                                    pathname: `${ROUTES.TRAINING}/${event.course.slug}`,
                                })
                            }
                        >
                            {event.course.title}
                        </ArchiveName>
                    ))}
                    {records?.length < recordsCount && (
                        <MoreLink onClick={() => handleShowMoreClick(PARTS.RECORDS)}>
                            <IconTemplate name={Icons.ArrowDown} /> {t('training.lecturer.showMore')}
                        </MoreLink>
                    )}
                </Archives>
            )}
        </Wrapper>
    );
};

export default TrainingLecturerTemplate;
